/**
 * Price Type Switch (Brutto/Netto)
 * Centralized logic for switching all prices between gross (inkl. MwSt) and net (zzgl. MwSt).
 * Swiss VAT rate: 8.1% (since 2024; changed 2026-10-08, was 7.7%)
 */

const PRICE_TYPE_CONFIG = {
  vatRate: 1.081,
  storageKey: 'priceOption',
  storageNameKey: 'priceOptionName',
  defaultOption: 'inkl',
};

const PRICE_SELECTORS = [
  '.price-item.price-item--regular',
  '.price-item.price-item--sale',
  '.cart-item .price--end',
  '.price-total-sum',
  '.totals__total-value',
].join(', ');

// One-time migration from sessionStorage to localStorage
(function () {
  var session = sessionStorage.getItem(PRICE_TYPE_CONFIG.storageKey);
  var local = localStorage.getItem(PRICE_TYPE_CONFIG.storageKey);
  if (session && !local) {
    localStorage.setItem(PRICE_TYPE_CONFIG.storageKey, session);
    var name = sessionStorage.getItem(PRICE_TYPE_CONFIG.storageNameKey);
    if (name) localStorage.setItem(PRICE_TYPE_CONFIG.storageNameKey, name);
  }
  sessionStorage.removeItem(PRICE_TYPE_CONFIG.storageKey);
  sessionStorage.removeItem(PRICE_TYPE_CONFIG.storageNameKey);
})();

function getStoredPriceOption() {
  return localStorage.getItem(PRICE_TYPE_CONFIG.storageKey) || PRICE_TYPE_CONFIG.defaultOption;
}

function storePriceOption(option, name) {
  localStorage.setItem(PRICE_TYPE_CONFIG.storageKey, option);
  localStorage.setItem(PRICE_TYPE_CONFIG.storageNameKey, name);
}

/* 2026-09-11: Beträge ohne unnötige «.00» (Nutzerwunsch «wenn der Preis auf .00 endet,
   soll das entfernt werden»). Dadurch stehen im Markup erstmals Brutto-Beträge wie
   «1,490 CHF» — ein Tausendertrenner OHNE Dezimalstellen. Der frühere Parser las das
   als 1.49 (Zweig «nur Komma»), rechnete also im Business-Modus grob falsch.

   betragZeichen() entscheidet deshalb am Abstand zum Ende: Stehen hinter dem letzten
   Trennzeichen genau drei Ziffern, ist es ein Tausendertrenner und das Dezimalzeichen
   das jeweils andere; sonst ist es das Dezimalzeichen. So bleibt die Umrechnung auch
   dann richtig, wenn ein Markt ein anderes Zahlenformat liefert.
   betragSchreiben() formatiert mit denselben Zeichen zurück und hängt Dezimalstellen
   nur an, wenn der Betrag krumm ist. */
function betragZeichen(roh) {
  var letzte = Math.max(roh.lastIndexOf('.'), roh.lastIndexOf(','));
  if (letzte === -1) return { dezimal: '.', tausender: ',' };
  var zeichen = roh.charAt(letzte);
  var stellen = roh.length - letzte - 1;
  if (stellen === 3) {
    return { dezimal: zeichen === ',' ? '.' : ',', tausender: zeichen };
  }
  return { dezimal: zeichen, tausender: zeichen === ',' ? '.' : ',' };
}

function betragLesen(roh, zeichen) {
  var ohneTausender = roh.split(zeichen.tausender).join('');
  return parseFloat(ohneTausender.split(zeichen.dezimal).join('.'));
}

function betragSchreiben(wert, zeichen) {
  var text = wert.toFixed(2);
  if (text.slice(-3) === '.00') text = text.slice(0, -3);
  var teile = text.split('.');
  teile[0] = teile[0].replace(/\B(?=(\d{3})+(?!\d))/g, zeichen.tausender);
  return teile.join(zeichen.dezimal);
}

/* 2026-09-11: Umrechnung EINES Elements — aus changePrice() herausgelöst, damit der
   Parse-Beobachter unten dieselbe Logik nutzt. Dank data-originalPrice beliebig oft aufrufbar. */
function convertPriceElement(el, option) {
    var priceText = el.textContent.trim();
    var match = priceText.match(/([\D]*)([\d,\.]+)([\D]*)/);
    if (!match) return;

    var prefix = match[1];
    var rawPrice = match[2];
    var zeichen = betragZeichen(rawPrice);
    var priceValue = betragLesen(rawPrice, zeichen);
    var suffix = match[3];
    if (isNaN(priceValue)) return;

    if (option === 'zzgl') {
      if (!el.dataset.originalPrice) {
        el.dataset.originalPrice = priceValue.toFixed(2);
        el.dataset.originalText = priceText;
      } else {
        priceValue = parseFloat(el.dataset.originalPrice);
      }
      el.textContent = prefix + betragSchreiben(priceValue / PRICE_TYPE_CONFIG.vatRate, zeichen) + suffix;
    } else {
      if (el.dataset.originalPrice) {
        el.textContent =
          el.dataset.originalText || prefix + betragSchreiben(parseFloat(el.dataset.originalPrice), zeichen) + suffix;
      }
    }
}

function changePrice(option) {
  if (!option) return;

  if (option === 'zzgl') {
    document.body.classList.add('netto');
    document.body.classList.remove('brutto');
  } else {
    document.body.classList.remove('netto');
    document.body.classList.add('brutto');
  }

  document.querySelectorAll(PRICE_SELECTORS).forEach(function (el) {
    convertPriceElement(el, option);
    el.setAttribute('data-wb-preis', '1');
  });

  syncDropdowns(option);

  /* 2026-09-10: Beträge wieder sichtbar (theme.liquid versteckt sie im Business-Modus,
     bis hier umgerechnet wurde). */
  document.documentElement.classList.remove('wb-preise-warten');
}

/* 2026-09-11: Netto-Beträge schon WÄHREND des Parsens umrechnen.
   Dieses Skript liegt jetzt inline am Anfang des <body> (theme.liquid, inline_asset_content),
   nicht mehr als deferred-Datei im Head — dort lief die Umrechnung erst bei DOMContentLoaded,
   und die Beträge standen bis dahin unsichtbar (gemessen: im DOM nach ~0,5 s, sichtbar erst
   nach 1,6–2,2 s). Im Business-Modus beobachtet ein MutationObserver das Dokument und rechnet
   jedes Preiselement um, sobald der Parser es fertig eingefügt hat. «Fertig» heisst: Das Element
   enthält den aktuellen Einfügepunkt (tiefster letzter Knoten des Dokuments) nicht mehr — der
   Parser hängt immer nur dort an. Ein noch offenes Element (Chunk-Grenze mitten im Betrag) darf
   nicht angefasst werden, sonst hängt der Parser den Rest der Zahl an den umgerechneten Text.
   Umgerechnete Elemente tragen data-wb-preis und werden damit sichtbar (theme.liquid versteckt
   nur Elemente ohne dieses Attribut). Der Beobachter läuft weiter und erfasst so auch später
   eingesetzte Preise (Warenkorb-Flyout, Suchvorschau, Farbwechsel); changePrice() bleibt die
   Sammel-Umrechnung. Im Home-Office-Modus wird gar nicht beobachtet. */
(function () {
  if (typeof MutationObserver !== 'function' || getStoredPriceOption() !== 'zzgl') return;
  var offen = [];

  function einfuegepunkt() {
    var n = document.documentElement;
    while (n && n.lastChild) n = n.lastChild;
    return n;
  }
  function vormerken(el) {
    if (el.getAttribute('data-wb-preis') !== '1' && offen.indexOf(el) === -1) offen.push(el);
  }
  function abarbeiten() {
    if (!offen.length) return;
    var netto = document.body && document.body.classList.contains('netto');
    var tail = einfuegepunkt();
    for (var i = offen.length - 1; i >= 0; i--) {
      var el = offen[i];
      if (el.contains(tail)) continue; /* Parser steht noch in diesem Element */
      offen.splice(i, 1);
      if (!el.isConnected) continue;
      if (netto) convertPriceElement(el, 'zzgl');
      el.setAttribute('data-wb-preis', '1');
    }
  }

  var beobachter = new MutationObserver(function (records) {
    for (var r = 0; r < records.length; r++) {
      var added = records[r].addedNodes;
      for (var a = 0; a < added.length; a++) {
        var n = added[a];
        if (n.nodeType === 1) {
          if (n.matches(PRICE_SELECTORS)) vormerken(n);
          var innen = n.querySelectorAll(PRICE_SELECTORS);
          for (var k = 0; k < innen.length; k++) vormerken(innen[k]);
        } else if (n.nodeType === 3 && n.parentElement) {
          var p = n.parentElement.closest(PRICE_SELECTORS);
          if (p) vormerken(p);
        }
      }
    }
    abarbeiten();
  });
  beobachter.observe(document.documentElement, { childList: true, subtree: true });
  document.querySelectorAll(PRICE_SELECTORS).forEach(vormerken);
  abarbeiten();
})();

function syncDropdowns(option) {
  document.querySelectorAll('.custom-dropdown').forEach(function (dropdown) {
    var selectedOption = dropdown.querySelector('.selected-option');
    var targetLi = dropdown.querySelector('.dropdown-li[data-value="' + option + '"]');
    if (selectedOption && targetLi) {
      selectedOption.textContent = targetLi.textContent;
    }
  });

  document.querySelectorAll('.checkmark').forEach(function (cm) {
    cm.classList.add('od-hidden');
  });
  document.querySelectorAll('.dropdown-li').forEach(function (item) {
    if (item.dataset.value === option) {
      var checkmark = item.previousElementSibling;
      if (checkmark && checkmark.classList.contains('checkmark')) {
        checkmark.classList.remove('od-hidden');
      }
    }
  });
}

// Apply stored preference on page load
document.addEventListener('DOMContentLoaded', function () {
  changePrice(getStoredPriceOption());

  // Re-apply after cart HTML replacement.
  // 2026-09-11: erst hier abonnieren — pubsub.js/constants.js laden deferred, also NACH diesem
  // (jetzt inline am Body-Anfang liegenden) Skript; beim Laden gäbe es subscribe noch nicht.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(function () {
        changePrice(getStoredPriceOption());
      }, 100);
    });
  }
});

// Expose globally for backward compatibility
window.changePrice = changePrice;
window.getStoredPriceOption = getStoredPriceOption;
window.storePriceOption = storePriceOption;
